import { useState } from "react";
import { C, FONT } from "./theme";

// ─── Reusable form primitives ────────────────────────────────────────────
// Shared by TrialSignupPage and OnboardingFlow so every input, button, and
// status indicator across the trial flow shares one visual language:
// consistent type, spacing, radii, and focus/hover/error/loading states.

const baseFieldStyle = (hasError, focused) => ({
  width: "100%",
  boxSizing: "border-box",
  fontFamily: FONT,
  fontSize: 15,
  color: C.text,
  background: "#fff",
  border: `1px solid ${hasError ? C.red : (focused ? C.accent : C.border)}`,
  borderRadius: 12,
  padding: "13px 14px",
  outline: "none",
  boxShadow: focused ? `0 0 0 4px ${hasError ? "rgba(224,57,62,0.12)" : "rgba(0,113,227,0.12)"}` : "none",
  transition: "border-color .15s ease, box-shadow .15s ease",
});

export function Label({ children, htmlFor, optional }) {
  return (
    <label htmlFor={htmlFor} style={{ display: "block", fontSize: 13, fontWeight: 600, color: C.text, marginBottom: 6, letterSpacing: "-.01px" }}>
      {children}
      {optional && <span style={{ fontWeight: 400, color: C.textFaint }}> (optional)</span>}
    </label>
  );
}

export function ValidationMessage({ children, tone = "error" }) {
  if (!children) return null;
  const color = tone === "error" ? C.red : tone === "success" ? C.green : C.textSub;
  return (
    <p style={{ margin: "6px 0 0", fontSize: 12.5, color, lineHeight: 1.5, display: "flex", alignItems: "flex-start", gap: 6 }}>
      {tone === "error" && <span aria-hidden style={{ fontSize: 12, lineHeight: "16px" }}>&#9888;</span>}
      <span>{children}</span>
    </p>
  );
}

export function TextField({ label, id, error, optional, ...props }) {
  const [focused, setFocused] = useState(false);
  return (
    <div>
      {label && <Label htmlFor={id} optional={optional}>{label}</Label>}
      <input
        id={id}
        style={baseFieldStyle(!!error, focused)}
        onFocus={(e) => { setFocused(true); props.onFocus && props.onFocus(e); }}
        onBlur={(e) => { setFocused(false); props.onBlur && props.onBlur(e); }}
        {...props}
      />
      <ValidationMessage>{error}</ValidationMessage>
    </div>
  );
}

export function PasswordField({ label, id, error, optional, value, onChange, ...props }) {
  const [focused, setFocused] = useState(false);
  const [visible, setVisible] = useState(false);
  return (
    <div>
      {label && <Label htmlFor={id} optional={optional}>{label}</Label>}
      <div style={{ position: "relative" }}>
        <input
          id={id}
          type={visible ? "text" : "password"}
          value={value}
          onChange={onChange}
          style={{ ...baseFieldStyle(!!error, focused), paddingRight: 46 }}
          onFocus={(e) => { setFocused(true); props.onFocus && props.onFocus(e); }}
          onBlur={(e) => { setFocused(false); props.onBlur && props.onBlur(e); }}
          {...props}
        />
        <button
          type="button"
          onClick={() => setVisible(v => !v)}
          aria-label={visible ? "Hide password" : "Show password"}
          style={{ position: "absolute", right: 6, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", padding: 8, color: C.textSub, fontSize: 12.5, fontWeight: 600, fontFamily: FONT }}
        >
          {visible ? "Hide" : "Show"}
        </button>
      </div>
      <ValidationMessage>{error}</ValidationMessage>
    </div>
  );
}

export function SelectField({ label, id, error, optional, options, ...props }) {
  const [focused, setFocused] = useState(false);
  return (
    <div>
      {label && <Label htmlFor={id} optional={optional}>{label}</Label>}
      <select
        id={id}
        style={{ ...baseFieldStyle(!!error, focused), appearance: "none", background: "#fff url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath d='M1 1l5 5 5-5' stroke='%236e6e73' stroke-width='1.5' fill='none' fill-rule='evenodd'/%3E%3C/svg%3E\") no-repeat right 14px center", cursor: "pointer" }}
        onFocus={(e) => { setFocused(true); props.onFocus && props.onFocus(e); }}
        onBlur={(e) => { setFocused(false); props.onBlur && props.onBlur(e); }}
        {...props}
      >
        {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
      <ValidationMessage>{error}</ValidationMessage>
    </div>
  );
}

export function Checkbox({ label, checked, onChange, id }) {
  return (
    <label htmlFor={id} style={{ display: "flex", alignItems: "flex-start", gap: 10, cursor: "pointer", fontSize: 13.5, color: C.textSub, lineHeight: 1.5 }}>
      <input id={id} type="checkbox" checked={checked} onChange={onChange} style={{ marginTop: 2, width: 16, height: 16, accentColor: C.accent, cursor: "pointer", flexShrink: 0 }} />
      <span>{label}</span>
    </label>
  );
}

export function Button({ children, variant = "primary", size = "md", loading, disabled, style, ...props }) {
  const sizes = { sm: { padding: "9px 18px", fontSize: 13.5 }, md: { padding: "13px 22px", fontSize: 15 }, lg: { padding: "16px 26px", fontSize: 16.5 } };
  const variants = {
    primary: { background: disabled || loading ? "#8fb9ea" : C.accent, color: "#fff", border: "none" },
    secondary: { background: "#fff", color: C.text, border: `1px solid ${C.border}` },
    ghost: { background: "transparent", color: C.accent, border: "none" },
  };
  return (
    <button
      disabled={disabled || loading}
      style={{
        fontFamily: FONT, fontWeight: 600, borderRadius: 980, cursor: (disabled || loading) ? "default" : "pointer",
        display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8,
        transition: "background .15s ease, transform .1s ease, opacity .15s ease",
        ...sizes[size], ...variants[variant], ...style,
      }}
      onMouseDown={(e) => { if (!disabled && !loading) e.currentTarget.style.transform = "scale(.98)"; }}
      onMouseUp={(e) => { e.currentTarget.style.transform = "scale(1)"; }}
      onMouseLeave={(e) => { e.currentTarget.style.transform = "scale(1)"; }}
      {...props}
    >
      {loading && <Spinner size={14} color={variant === "primary" ? "#fff" : C.accent} />}
      {children}
    </button>
  );
}

export function Spinner({ size = 16, color = C.accent }) {
  return (
    <span style={{
      display: "inline-block", width: size, height: size,
      border: `2px solid ${color === "#fff" ? "rgba(255,255,255,.35)" : "rgba(0,113,227,.2)"}`,
      borderTopColor: color, borderRadius: "50%", animation: "fk-spin .7s linear infinite",
    }} />
  );
}

// Global keyframes for Spinner — injected once, harmless if included
// multiple times since the selector is identical.
export function FormKitStyles() {
  return <style>{`@keyframes fk-spin { to { transform: rotate(360deg); } }`}</style>;
}

// Horizontal step indicator for the onboarding flow: filled circle + label
// per step, connecting line, current step highlighted.
export function ProgressSteps({ steps, currentIndex }) {
  return (
    <div style={{ display: "flex", alignItems: "center", width: "100%" }}>
      {steps.map((label, i) => {
        const done = i < currentIndex;
        const active = i === currentIndex;
        return (
          <div key={label} style={{ display: "flex", alignItems: "center", flex: i === steps.length - 1 ? "0 0 auto" : 1 }}>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8, minWidth: 0 }}>
              <div style={{
                width: 28, height: 28, borderRadius: "50%", flexShrink: 0,
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 12.5, fontWeight: 700,
                background: done ? C.accent : active ? "#fff" : C.bgAlt,
                color: done ? "#fff" : active ? C.accent : C.textFaint,
                border: active ? `2px solid ${C.accent}` : "2px solid transparent",
                transition: "all .2s ease",
              }}>
                {done ? "✓" : i + 1}
              </div>
              <span style={{ fontSize: 11.5, fontWeight: active ? 700 : 500, color: active ? C.text : C.textFaint, whiteSpace: "nowrap" }}>{label}</span>
            </div>
            {i < steps.length - 1 && (
              <div style={{ flex: 1, height: 2, background: done ? C.accent : C.borderSoft, margin: "0 8px 22px", transition: "background .2s ease" }} />
            )}
          </div>
        );
      })}
    </div>
  );
}

// Small pill badge shown inside the app shell to communicate remaining
// trial time / expiration / conversion state at a glance.
export function TrialStatusBadge({ status, daysRemaining, onUpgrade }) {
  if (!status || status === "CONVERTED") return null;
  const map = {
    TRIAL_NOT_STARTED: { label: "Trial not started", bg: C.bgAlt, fg: C.textSub },
    TRIAL_ACTIVE:      { label: `${daysRemaining ?? "—"} day${daysRemaining === 1 ? "" : "s"} left in trial`, bg: C.accentSoft, fg: C.accent },
    TRIAL_EXPIRING:    { label: `Trial ends in ${daysRemaining ?? "—"} day${daysRemaining === 1 ? "" : "s"}`, bg: C.amberSoft, fg: C.amber },
    TRIAL_EXPIRED:     { label: "Trial expired", bg: C.redSoft, fg: C.red },
  };
  const cfg = map[status] || map.TRIAL_ACTIVE;
  return (
    <button onClick={onUpgrade} style={{
      display: "inline-flex", alignItems: "center", gap: 6, background: cfg.bg, color: cfg.fg,
      border: "none", borderRadius: 980, padding: "6px 12px", fontSize: 12, fontWeight: 700,
      fontFamily: FONT, cursor: onUpgrade ? "pointer" : "default", whiteSpace: "nowrap",
    }}>
      {cfg.label}
      {status !== "TRIAL_NOT_STARTED" && <span style={{ opacity: .7 }}>&rarr;</span>}
    </button>
  );
}
